import { Directive, ElementRef, OnInit, OnDestroy, Input } from '@angular/core';
import * as THREE from 'three';

@Directive({
  selector: '[threeGradientMap]',
  standalone: true
})
export class ThreeGradientMapDirective implements OnInit, OnDestroy {
  @Input() imageUrl!: string;
  @Input() gradientColors = {
    dark: new THREE.Color(0x001f19),      // Very dark green (almost black)
    light: new THREE.Color(0x00574a)      // Mid-tone green (lighter than CBRE dark green)
  };

  private scene!: THREE.Scene;
  private camera!: THREE.OrthographicCamera;
  private renderer!: THREE.WebGLRenderer;
  private mesh!: THREE.Mesh;
  private animationId!: number;
  private resizeObserver!: ResizeObserver;
  private startTime = Date.now();

  constructor(private el: ElementRef<HTMLElement>) {}

  ngOnInit() {
    try {
      this.initThreeJS();
      this.setupResizeObserver();
      this.animate();
    } catch {
      // WebGL not available — skip the 3D effect silently
    }
  }

  ngOnDestroy() {
    try { if (this.animationId) cancelAnimationFrame(this.animationId); } catch { /* ignore */ }
    try { if (this.resizeObserver) this.resizeObserver.disconnect(); } catch { /* ignore */ }
    try { if (this.renderer) this.renderer.dispose(); } catch { /* ignore */ }
    try { if (this.mesh?.geometry) this.mesh.geometry.dispose(); } catch { /* ignore */ }
    try { if (this.mesh?.material) (this.mesh.material as THREE.Material).dispose(); } catch { /* ignore */ }
  }

  private initThreeJS() {
    const container = this.el.nativeElement;
    const rect = container.getBoundingClientRect();

    // Create scene
    this.scene = new THREE.Scene();

    // Create orthographic camera
    this.camera = new THREE.OrthographicCamera(
      -1, 1, 1, -1, 0, 1
    );

    // Pre-create a WebGL1 context to prevent Three.js from attempting WebGL2 first
    // (avoids console errors on systems where WebGL2 is blocked, e.g. Firefox enterprise)
    const rendererCanvas = document.createElement('canvas');
    const glContext = (rendererCanvas.getContext('webgl') ?? rendererCanvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!glContext) return; // WebGL unavailable — degrade silently

    // Create WebGL renderer using the pre-created WebGL1 context
    this.renderer = new THREE.WebGLRenderer({
      canvas: rendererCanvas,
      context: glContext,
      alpha: true,
      antialias: true
    });
    this.renderer.setSize(rect.width || 0, rect.height || 0);
    this.renderer.setPixelRatio(window.devicePixelRatio || 1);
    
    // Style renderer canvas
    this.renderer.domElement.style.position = 'absolute';
    this.renderer.domElement.style.top = '0';
    this.renderer.domElement.style.left = '0';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.pointerEvents = 'none';

    container.appendChild(this.renderer.domElement);

    if (!this.imageUrl) return;

    // Load texture and create material
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load(this.imageUrl, (texture) => {
      try {
        const material = new THREE.ShaderMaterial({
        uniforms: {
          uTexture: { value: texture },
          uTime: { value: 0 },
          uDarkColor: { value: this.gradientColors.dark },
          uLightColor: { value: this.gradientColors.light },
          uMixStrength: { value: 0.7 } // How much gradient effect vs original image
        },
        vertexShader: `
          varying vec2 vUv;
          
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform sampler2D uTexture;
          uniform float uTime;
          uniform vec3 uDarkColor;
          uniform vec3 uLightColor;
          uniform float uMixStrength;

          varying vec2 vUv;

          void main() {
            // Sample the original texture
            vec4 texColor = texture2D(uTexture, vUv);

            // Convert to grayscale using luminosity
            float luminosity = dot(texColor.rgb, vec3(0.299, 0.587, 0.114));

            // Apply high contrast
            // Adjust contrast: values below 0.5 go darker, above 0.5 go lighter
            float contrast = 1.8; // High contrast multiplier
            luminosity = clamp((luminosity - 0.5) * contrast + 0.5, 0.0, 1.0);

            // Create pure grayscale
            vec3 grayscale = vec3(luminosity);

            // Apply gradient map to grayscale
            vec3 gradientColor = mix(uDarkColor, uLightColor, luminosity);

            // Mix between grayscale and gradient-mapped version
            vec3 finalColor = mix(grayscale, gradientColor, uMixStrength);

            // Add subtle vignette for depth
            float vignette = smoothstep(0.8, 0.2, length(vUv - 0.5));
            finalColor *= vignette * 0.5 + 0.5;

            gl_FragColor = vec4(finalColor, texColor.a);
          }
        `
      });

      // Create plane geometry
      const geometry = new THREE.PlaneGeometry(2, 2);
      this.mesh = new THREE.Mesh(geometry, material);
      this.scene.add(this.mesh);
      } catch { /* ignore material/mesh creation errors */ }
    }, undefined, () => { /* texture load failed — degrade silently */ });
  }

  private setupResizeObserver() {
    try {
      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          try { this.onResize(); } catch { /* ignore */ }
        });
      });
      this.resizeObserver.observe(this.el.nativeElement);
    } catch { /* ResizeObserver not supported */ }
  }

  private onResize() {
    if (!this.renderer) return;
    try {
      const rect = this.el.nativeElement.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        this.renderer.setSize(rect.width, rect.height);
      }
    } catch { /* ignore resize errors */ }
  }

  private animate = () => {
    if (!this.renderer) return;
    this.animationId = requestAnimationFrame(this.animate);
    try {
      if (this.mesh?.material) {
        const material = this.mesh.material as THREE.ShaderMaterial;
        const elapsed = (Date.now() - this.startTime) * 0.001;
        material.uniforms['uTime'].value = elapsed;
      }
      this.renderer.render(this.scene, this.camera);
    } catch { /* ignore render errors */ }
  };
}
